import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { transactionsService } from '../services/api';
import { formatCurrency } from '../utils/invoiceHelpers';
import StatusBadge from './shared/StatusBadge';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function MerchantProfile({ mockUsers }) {
  const { userId } = useParams();
  const location = useLocation();
  const merchant = mockUsers.find((m) => m.id === parseInt(userId));

  // Get route prefix from current location (e.g., /so/merchants or /admin/merchants)
  const routePrefix = location.pathname.startsWith('/so/') ? '/so' : '/admin';

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('Month'); // Default filter: Month

  // Generate mock sales data for the past year
  const generateMockSalesData = () => {
    const salesData = [];
    const startDate = new Date('2024-04-01'); // Start from April 1, 2024
    const endDate = new Date('2025-03-31'); // End at March 31, 2025

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const date = new Date(d);
      const sales = Math.floor(Math.random() * 1000) + 500; // Random sales between 500 and 1500
      salesData.push({ date, sales });
    }

    return salesData;
  };

  // Filter sales data based on the selected time period
  const filterSalesData = (salesData, filter) => {
    const filteredData = [];
    const labels = [];

    if (filter === 'Day') {
      // Last 30 days
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 29); // 30 days including today
      for (let d = new Date(startDate); d <= new Date(); d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        const daySales = salesData.filter((entry) => entry.date.toISOString().split('T')[0] === dateStr);
        const totalSales = daySales.reduce((sum, entry) => sum + entry.sales, 0);
        filteredData.push(totalSales);
        labels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }
    } else if (filter === 'Week') {
      // Last 12 weeks
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7 * 11); // 12 weeks including this week
      for (let d = new Date(startDate); d <= new Date(); d.setDate(d.getDate() + 7)) {
        const weekStart = new Date(d);
        const weekEnd = new Date(d);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const weekSales = salesData.filter((entry) => entry.date >= weekStart && entry.date <= weekEnd);
        const totalSales = weekSales.reduce((sum, entry) => sum + entry.sales, 0);
        filteredData.push(totalSales);
        labels.push(`Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
      }
    } else if (filter === 'Month') {
      // Last 12 months
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 11); // 12 months including this month
      for (let d = new Date(startDate); d <= new Date(); d.setMonth(d.getMonth() + 1)) {
        const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
        const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
        const monthSales = salesData.filter((entry) => entry.date >= monthStart && entry.date <= monthEnd);
        const totalSales = monthSales.reduce((sum, entry) => sum + entry.sales, 0);
        filteredData.push(totalSales);
        labels.push(d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
      }
    } else if (filter === 'Quarter') {
      // Last 4 quarters
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 9); // 4 quarters (3 months each)
      for (let q = 0; q < 4; q++) {
        const quarterStart = new Date(startDate.getFullYear(), startDate.getMonth() + q * 3, 1);
        const quarterEnd = new Date(quarterStart.getFullYear(), quarterStart.getMonth() + 3, 0);
        const quarterSales = salesData.filter((entry) => entry.date >= quarterStart && entry.date <= quarterEnd);
        const totalSales = quarterSales.reduce((sum, entry) => sum + entry.sales, 0);
        filteredData.push(totalSales);
        const quarterNum = Math.floor((quarterStart.getMonth() + 3) / 3);
        labels.push(`Q${quarterNum} ${quarterStart.getFullYear()}`);
      }
    } else if (filter === 'Year') {
      // Last 2 years (2024 and 2025)
      for (let year = 2024; year <= 2025; year++) {
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);
        const yearSales = salesData.filter((entry) => entry.date >= yearStart && entry.date <= yearEnd);
        const totalSales = yearSales.reduce((sum, entry) => sum + entry.sales, 0);
        filteredData.push(totalSales);
        labels.push(year.toString());
      }
    }

    return { labels, data: filteredData };
  };

  // Generate and filter sales data
  const mockSalesData = generateMockSalesData();
  const { labels, data } = filterSalesData(mockSalesData, filter);

  // Chart data
  const chartData = {
    labels,
    datasets: [
      {
        label: 'Sales ($)',
        data,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        pointRadius: 5,
        pointHoverRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Sales by ${filter}`,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    scales: {
      x: {
        title: {
          display: true,
          text: filter,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Sales ($)',
        },
      },
    },
    hover: {
      mode: 'nearest',
      intersect: false,
    },
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await transactionsService.getTransactions();
        setTransactions(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch transactions: ' + err.message);
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []); // Empty dependency array ensures this runs only once on mount

  if (!merchant) {
    return <div className="text-red-500">Merchant not found.</div>;
  }

  return (
    <div>
      {/* Breadcrumb Trail */}
      <div className="mb-4">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          <Link to={`${routePrefix}/merchants`} className="hover:text-blue-600">Merchants</Link>
          <span>›</span>
          <span>{merchant.name}</span>
        </nav>
      </div>

      <h2 className="text-xl md:text-2xl font-bold mb-4 text-gray-800">{merchant.name}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Merchant Details Card */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-medium mb-4 text-gray-700">Merchant Details</h3>
          <p><strong>Business Name:</strong> {merchant.businessName}</p>
          <p><strong>Contact:</strong> {merchant.contact}</p>
          <p><strong>Contact Phone:</strong> {merchant.contactPhone}</p>
          <p><strong>Email:</strong> {merchant.email}</p>
          <p><strong>MID#:</strong> {merchant.mid}</p>
          <p><strong>PCI Compliance:</strong> 
            <StatusBadge 
              type="compliance_status" 
              value={merchant.pciCompliant}
              size="xs"
            />
          </p>
          <p><strong>Total Sales:</strong> {formatCurrency(merchant.totalSales)}</p>
        </div>

        {/* Transactions Card */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md overflow-x-auto">
          <h3 className="text-lg font-medium mb-4 text-gray-700">Transactions</h3>
          {loading ? (
            <p className="text-gray-500">Loading transactions...</p>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : transactions.length === 0 ? (
            <p className="text-gray-500">No transactions found for this merchant.</p>
          ) : (
            <table className="w-full border-separate border-spacing-0 min-w-[600px]">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border-t border-l border-r border-gray-200 p-2 md:p-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase rounded-tl-lg">Transaction ID</th>
                  <th className="border-t border-l border-r border-gray-200 p-2 md:p-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase">Date</th>
                  <th className="border-t border-l border-r border-gray-200 p-2 md:p-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase">Card Type</th>
                  <th className="border-t border-l border-r border-gray-200 p-2 md:p-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="border-t border-l border-r border-gray-200 p-2 md:p-3 text-left text-xs md:text-sm font-semibold text-gray-600 uppercase rounded-tr-lg">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn, index) => (
                  <tr key={txn.ssl_txn_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border-t border-l border-r border-gray-200 p-2 md:p-3">{txn.ssl_txn_id}</td>
                    <td className="border-t border-l border-r border-gray-200 p-2 md:p-3">{txn.ssl_txn_time}</td>
                    <td className="border-t border-l border-r border-gray-200 p-2 md:p-3">{txn.ssl_card_short_description}</td>
                    <td className="border-t border-l border-r border-gray-200 p-2 md:p-3">{formatCurrency(txn.ssl_amount)}</td>
                    <td className="border-t border-l border-r border-gray-200 p-2 md:p-3">{txn.ssl_result_message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Sales Chart Card */}
      <div className="mt-4 md:mt-6 bg-white p-4 md:p-6 rounded-lg shadow-md">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
          <h3 className="text-lg font-medium text-gray-700 mb-2 md:mb-0">Sales Overview</h3>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-auto"
          >
            <option value="Day">Day</option>
            <option value="Week">Week</option>
            <option value="Month">Month</option>
            <option value="Quarter">Quarter</option>
            <option value="Year">Year</option>
          </select>
        </div>
        <div className="w-full">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
}

export default MerchantProfile;